import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, COUNTRIES, fmtINR } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import {
  GraduationCap, Globe2, BookOpen, Wallet, PiggyBank, ClipboardList, SlidersHorizontal,
} from "lucide-react";

const COURSES = [
  "Computer Science", "Engineering", "Business", "Economics", "Medicine", "Law",
  "Architecture", "Design", "Mathematics", "Physics", "Biology", "Psychology",
  "Liberal Arts", "Politics & International Relations",
];

const SUBJECTS = [
  "Mathematics AA", "Mathematics AI", "Physics", "Chemistry", "Biology",
  "Economics", "Business Management", "Computer Science", "English Lit",
  "English Lang & Lit", "History", "Psychology", "Geography", "Visual Arts",
];

const LANGUAGES = [
  "English-only", "Open to German", "Open to French", "Open to Dutch", "Bilingual comfortable",
];

const RISK_LEVELS = ["Low", "Medium", "High"];
const SCHOLARSHIP = ["Yes — likely eligible", "Maybe — exploring", "No — not pursuing"];
const TIMELINES = ["Aug 2026 intake", "Jan 2027 intake", "Aug 2027 intake", "Aug 2028 intake"];

// Financial commitment options
const LOAN_OPTIONS = [
  { value:  2500000, label: "₹25L"  },
  { value:  5000000, label: "₹50L"  },
  { value:  7500000, label: "₹75L"  },
  { value: 10000000, label: "₹1 Cr"  },
  { value: 15000000, label: "₹1.5 Cr" },
  { value: 20000000, label: "₹2 Cr"  },
];
const SCHOLARSHIP_PCT_OPTIONS = [5, 10, 15, 20, 25, 30, 50, 100];

export default function ProfilePage() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [form, setForm] = useState({
    predicted_ib_score: 38,
    grade: "IBDP Year 2",
    hl_subjects: [],
    sl_subjects: [],
    target_countries: [],
    intended_courses: [],
    budget_inr_min: 4000000,
    budget_inr_max: 12000000,
    career_interests: "",
    sat_score: null,
    ielts_score: null,
    language_preferences: [],
    parent_expectations: "",
    scholarship_eligibility: "",
    timeline: "",
    risk_tolerance: "",
    parent_committed_inr: null,
    loan_willingness_inr: null,
    expected_scholarship_pct: null,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/profile");
      if (data && data.predicted_ib_score) setForm(f => ({ ...f, ...data }));
    })();
  }, []);

  const toggle = (key, val) => {
    setForm(f => {
      const arr = f[key].includes(val) ? f[key].filter(x => x !== val) : [...f[key], val];
      return { ...f, [key]: arr };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile", form);
      // Refresh auth context so profile_completed flips to true and the first-login
      // onboarding redirect no longer applies on subsequent navigations.
      await refresh();
      nav("/dashboard");
    } finally {
      setSaving(false);
    }
  };

  // ---- Accordion state: only one section open at a time ----
  const [openId, setOpenId] = useState("academic");
  const toggleSection = (id) => setOpenId(curr => (curr === id ? null : id));

  // ---- Per-section completion status ----
  const sectionStatuses = useMemo(() => {
    const filled = (v) => {
      if (v === null || v === undefined) return false;
      if (typeof v === "string") return v.trim().length > 0;
      if (typeof v === "number") return v > 0;
      if (Array.isArray(v)) return v.length > 0;
      return Boolean(v);
    };
    const status = (vals) => {
      const filledCount = vals.filter(filled).length;
      if (filledCount === 0) return "notStarted";
      if (filledCount === vals.length) return "complete";
      return "inProgress";
    };
    return {
      academic:    status([form.predicted_ib_score, form.grade, form.hl_subjects, form.sl_subjects]),
      countries:   status([form.target_countries]),
      courses:     status([form.intended_courses, form.career_interests]),
      budget:      status([form.budget_inr_min, form.budget_inr_max]),
      commitments: status([form.parent_committed_inr, form.loan_willingness_inr, form.expected_scholarship_pct]),
      scores:      status([form.sat_score, form.ielts_score]),
      preferences: status([form.language_preferences, form.parent_expectations, form.scholarship_eligibility, form.timeline, form.risk_tolerance]),
    };
  }, [form]);

  return (
    <div className="min-h-screen bg-white lg:pl-[240px]">
      <Nav />
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-12">
        <div className="text-label text-[var(--s400)] mb-3" data-testid="profile-eyebrow">
          Student Profile
        </div>
        <h1 className="text-h1 text-[var(--s900)]" data-testid="profile-headline">
          Tell us about your child.
        </h1>
        <p className="text-body-sm mt-3 max-w-xl text-[var(--s500)]">
          The more we know, the more honest the recommendations.{" "}
          <span className="text-[var(--ind)] font-medium">We never share this data.</span>
        </p>

        <div className="mt-10 space-y-3 profile-form">
          <Section
            id="academic" title="Academic profile" icon={GraduationCap}
            status={sectionStatuses.academic} open={openId === "academic"} onToggle={toggleSection}
          >
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="Predicted IB total ( / 45 )">
                <input
                  type="number"
                  min={24}
                  max={45}
                  value={form.predicted_ib_score || ""}
                  data-testid="prof-ib"
                  onChange={e => setForm({ ...form, predicted_ib_score: parseInt(e.target.value || "0", 10) })}
                />
              </Field>
              <Field label="Grade">
                <select
                  value={form.grade || ""}
                  data-testid="prof-grade"
                  onChange={e => setForm({ ...form, grade: e.target.value })}
                >
                  <option>IBDP Year 1</option>
                  <option>IBDP Year 2</option>
                  <option>Pre-IB / IGCSE</option>
                  <option>Completed</option>
                </select>
              </Field>
            </div>
            <div className="mt-6">
              <div className="eyebrow mb-3">HL Subjects (choose 3)</div>
              <Chips items={SUBJECTS} selected={form.hl_subjects} onToggle={v => toggle("hl_subjects", v)} testid="hl" />
            </div>
            <div className="mt-6">
              <div className="eyebrow mb-3">SL Subjects (choose 3)</div>
              <Chips items={SUBJECTS} selected={form.sl_subjects} onToggle={v => toggle("sl_subjects", v)} testid="sl" />
            </div>
          </Section>

          <Section
            id="countries" title="Target countries" icon={Globe2}
            status={sectionStatuses.countries} open={openId === "countries"} onToggle={toggleSection}
          >
            <Chips
              items={COUNTRIES.map(c => c.code)}
              selected={form.target_countries}
              labels={Object.fromEntries(COUNTRIES.map(c => [c.code, `${c.flag} ${c.name}`]))}
              onToggle={v => toggle("target_countries", v)}
              testid="country"
            />
          </Section>

          <Section
            id="courses" title="Intended course(s)" icon={BookOpen}
            status={sectionStatuses.courses} open={openId === "courses"} onToggle={toggleSection}
          >
            <Chips items={COURSES} selected={form.intended_courses} onToggle={v => toggle("intended_courses", v)} testid="course" />
            <div className="mt-6">
              <Field label="Career interests / notes">
                <textarea
                  rows={3}
                  value={form.career_interests || ""}
                  data-testid="prof-career"
                  placeholder="e.g., interested in AI research; possibly a startup later; open to a quant career"
                  onChange={e => setForm({ ...form, career_interests: e.target.value })}
                />
              </Field>
            </div>
          </Section>

          <Section
            id="budget" title="Family budget (total, INR)" icon={Wallet}
            status={sectionStatuses.budget} open={openId === "budget"} onToggle={toggleSection}
          >
            <p className="text-body-sm text-[var(--s600)] mb-5 leading-relaxed">
              Be honest. We use this to flag programmes that may stretch you, and to surface scholarship realism.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <Field label={<>Minimum · <span className="text-[var(--ind)]">{fmtINR(form.budget_inr_min || 0)}</span></>}>
                <input
                  type="range"
                  min={2000000}
                  max={30000000}
                  step={500000}
                  value={form.budget_inr_min || 0}
                  data-testid="prof-bud-min"
                  onChange={e => setForm({ ...form, budget_inr_min: parseInt(e.target.value, 10) })}
                />
              </Field>
              <Field label={<>Maximum · <span className="text-[var(--ind)]">{fmtINR(form.budget_inr_max || 0)}</span></>}>
                <input
                  type="range"
                  min={3000000}
                  max={40000000}
                  step={500000}
                  value={form.budget_inr_max || 0}
                  data-testid="prof-bud-max"
                  onChange={e => setForm({ ...form, budget_inr_max: parseInt(e.target.value, 10) })}
                />
              </Field>
            </div>
          </Section>

          <Section
            id="commitments" title="Financial commitments" icon={PiggyBank}
            status={sectionStatuses.commitments} open={openId === "commitments"} onToggle={toggleSection}
          >
            <p className="text-body-sm text-[var(--s600)] mb-5 leading-relaxed">
              These three numbers help Unimora AI Mentor assess what&apos;s actually viable for your family — beyond a budget range.
            </p>
            <div className="space-y-6">
              <Field label={<>Parental contribution (confirmed) · <span className="text-[var(--ind)]">{fmtINR(form.parent_committed_inr || 0)}</span></>}>
                <input
                  type="range"
                  min={0}
                  max={30000000}
                  step={500000}
                  value={form.parent_committed_inr || 0}
                  data-testid="prof-parent-committed"
                  onChange={e => setForm({ ...form, parent_committed_inr: parseInt(e.target.value, 10) })}
                />
                <div className="text-label text-[var(--s500)] mt-1.5">Money you&apos;re certain about — independent of scholarships or loans.</div>
              </Field>
              <Field label="Maximum loan you're open to">
                <div className="flex flex-wrap gap-2" data-testid="prof-loan-willingness">
                  {LOAN_OPTIONS.map(opt => {
                    const on = form.loan_willingness_inr === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setForm({ ...form, loan_willingness_inr: opt.value })}
                        data-testid={`chip-loan-${opt.value}`}
                        className={`text-body-sm transition-colors px-4 py-2 rounded-[var(--r8)] border ${on ? "bg-[var(--ind)] text-white border-[var(--ind)] font-medium" : "bg-white text-[var(--s700)] border-[var(--s200)]"}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </Field>
              <Field label="Minimum scholarship % needed for a stretch university">
                <div className="flex flex-wrap gap-2" data-testid="prof-expected-scholarship">
                  {SCHOLARSHIP_PCT_OPTIONS.map(pct => {
                    const on = form.expected_scholarship_pct === pct;
                    return (
                      <button
                        key={pct}
                        type="button"
                        onClick={() => setForm({ ...form, expected_scholarship_pct: pct })}
                        data-testid={`chip-scholarship-pct-${pct}`}
                        className={`text-body-sm transition-colors px-4 py-2 rounded-[var(--r8)] border ${on ? "bg-[var(--ind)] text-white border-[var(--ind)] font-medium" : "bg-white text-[var(--s700)] border-[var(--s200)]"}`}
                      >
                        {pct}%
                      </button>
                    );
                  })}
                </div>
                <div className="text-label text-[var(--s500)] mt-1.5">Below this threshold, the Coach will flag a stretch pick as financially unrealistic.</div>
              </Field>
            </div>
          </Section>

          <Section
            id="scores" title="Test Scores (optional)" icon={ClipboardList}
            status={sectionStatuses.scores} open={openId === "scores"} onToggle={toggleSection}
          >
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="SAT score (out of 1600)">
                <input
                  type="number"
                  min={0} max={1600} step={10}
                  value={form.sat_score ?? ""}
                  data-testid="prof-sat"
                  onChange={e => setForm({ ...form, sat_score: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                  placeholder="e.g. 1450"
                />
              </Field>
              <Field label="IELTS score (out of 9.0)">
                <input
                  type="number"
                  min={0} max={9} step={0.5}
                  value={form.ielts_score ?? ""}
                  data-testid="prof-ielts"
                  onChange={e => setForm({ ...form, ielts_score: e.target.value === "" ? null : parseFloat(e.target.value) })}
                  placeholder="e.g. 7.5"
                />
              </Field>
            </div>
          </Section>

          <Section
            id="preferences" title="Preferences & Constraints" icon={SlidersHorizontal}
            status={sectionStatuses.preferences} open={openId === "preferences"} onToggle={toggleSection}
          >
            <div className="space-y-6">
              <Field label="Language comfort">
                <Chips
                  items={LANGUAGES}
                  selected={form.language_preferences}
                  onToggle={(v) => toggle("language_preferences", v)}
                  testid="lang"
                />
              </Field>
              <Field label="Parent expectations (free-text)">
                <textarea
                  rows={3}
                  value={form.parent_expectations}
                  data-testid="prof-parent-exp"
                  onChange={e => setForm({ ...form, parent_expectations: e.target.value })}
                  placeholder="e.g. Top-100 university, English-speaking country, employability over prestige"
                  className="w-full"
                />
              </Field>
              <div className="grid md:grid-cols-3 gap-6">
                <Field label="Scholarship eligibility">
                  <select
                    value={form.scholarship_eligibility}
                    data-testid="prof-scholarship"
                    onChange={e => setForm({ ...form, scholarship_eligibility: e.target.value })}
                  >
                    <option value="">Select…</option>
                    {SCHOLARSHIP.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Timeline / intake">
                  <select
                    value={form.timeline}
                    data-testid="prof-timeline"
                    onChange={e => setForm({ ...form, timeline: e.target.value })}
                  >
                    <option value="">Select…</option>
                    {TIMELINES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Risk tolerance">
                  <select
                    value={form.risk_tolerance}
                    data-testid="prof-risk"
                    onChange={e => setForm({ ...form, risk_tolerance: e.target.value })}
                  >
                    <option value="">Select…</option>
                    {RISK_LEVELS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
              </div>
            </div>
          </Section>

          <div className="flex items-center gap-5 pt-4">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="prof-save"
              className="text-body-sm font-medium bg-[var(--ind)] text-white px-7 py-[11px] rounded-[var(--r8)] border-0 transition-colors hover:bg-[var(--ind-hover)] disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save profile"}
            </button>
            <button
              type="button"
              onClick={() => nav("/dashboard")}
              data-testid="prof-cancel"
              className="text-body-sm text-[var(--s400)] bg-transparent border-0 p-0 transition-colors hover:text-[var(--s900)]"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

// Per-section icon tint palette — soft tinted backgrounds with darker icon colour.
const ICON_TINT = {
  academic:    { bg: "#EEF2FF", fg: "#4F46E5" }, // indigo
  countries:   { bg: "#ECFDF5", fg: "#059669" }, // emerald
  courses:     { bg: "#F5F3FF", fg: "#7C3AED" }, // violet
  budget:      { bg: "#FFFBEB", fg: "#D97706" }, // amber
  commitments: { bg: "#FFF1F2", fg: "#E11D48" }, // rose
  scores:      { bg: "#F0F9FF", fg: "#0284C7" }, // sky
  preferences: { bg: "#FDF4FF", fg: "#C026D3" }, // fuchsia
};

function Section({ id, title, icon: Icon, status, open, onToggle, children }) {
  const STATUS_STYLES = {
    complete:   { label: "Complete",    bg: "bg-[var(--success-soft)]", text: "text-[var(--success)]" },
    inProgress: { label: "In progress", bg: "bg-[var(--warning-soft)]", text: "text-[var(--warning)]" },
    notStarted: { label: "Not started", bg: "bg-slate-100", text: "text-[var(--s500)]" },
  };
  const s = STATUS_STYLES[status] || STATUS_STYLES.notStarted;
  const tint = ICON_TINT[id] || ICON_TINT.academic;
  return (
    <div
      className="bg-white rounded-[var(--r12)] overflow-hidden"
      style={{ border: "0.5px solid #E8EAF0" }}
      data-testid={`section-card-${id}`}
    >
      <button
        type="button"
        onClick={() => onToggle(id)}
        data-testid={`section-toggle-${id}`}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-slate-50/40 transition-colors"
      >
        <span
          className="w-[34px] h-[34px] rounded-[var(--r8)] flex items-center justify-center shrink-0"
          style={{ background: tint.bg, color: tint.fg }}
        >
          <Icon size={17} strokeWidth={1.9} />
        </span>
        <span className="flex-1 text-body-sm font-medium text-[var(--s900)]">
          {title}
        </span>
        <span
          data-testid={`section-status-${id}`}
          className={`text-micro font-semibold uppercase tracking-[0.05em] px-2.5 py-1 rounded-full ${s.bg} ${s.text}`}
        >
          {s.label}
        </span>
        <ChevronCSS open={open} />
      </button>
      {open && (
        <div
          className="px-5 pb-5"
          style={{ borderTop: "0.5px solid #E8EAF0", background: tint.bg }}
        >
          <div className="pt-5">{children}</div>
        </div>
      )}
    </div>
  );
}

function ChevronCSS({ open }) {
  // Two short CSS lines forming a V; rotates 180° when open.
  return (
    <span
      data-testid="section-chevron"
      className={`relative inline-block w-4 h-4 transition-transform duration-200 shrink-0 ${open ? "rotate-180" : ""}`}
      aria-hidden
    >
      <span
        className="absolute left-[1px] top-[6px] w-[9px] h-[1.5px] bg-slate-500 rounded-full"
        style={{ transform: "rotate(40deg)", transformOrigin: "left center" }}
      />
      <span
        className="absolute right-[1px] top-[6px] w-[9px] h-[1.5px] bg-slate-500 rounded-full"
        style={{ transform: "rotate(-40deg)", transformOrigin: "right center" }}
      />
    </span>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <div className="text-label text-[var(--s400)] mb-2">
        {label}
      </div>
      {children}
    </div>
  );
}
function Chips({ items, selected, onToggle, labels, testid }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map(v => {
        const on = selected.includes(v);
        return (
          <button
            key={v}
            type="button"
            onClick={() => onToggle(v)}
            data-testid={`chip-${testid}-${v}`}
            className={`text-body-sm transition-colors px-3.5 py-1.5 rounded-full border ${on ? "bg-[var(--ind)] text-white border-[var(--ind)] font-medium" : "bg-white text-[var(--s700)] border-[var(--s200)]"}`}
          >
            {labels?.[v] || v}
          </button>
        );
      })}
    </div>
  );
}
